import { useEffect, useRef, useState } from "react"
import { Link } from "react-router"
import { toast } from "sonner"
import {
  ArrowRight,
  Bell01,
  CheckCircle,
  Coins03,
  MessageChatCircle,
  UserPlus01,
  X,
} from "@untitledui/icons"

import { Sheet } from "@/components/layout/sheet"
import { useActivity, useMarkActivityRead } from "@/lib/api/use-activity"
import type { ActivityEvent } from "@/lib/api/activity"
import { cx } from "@/lib/utils/cx"

/* ═══════════════ NOTIFICATIONS ═══════════════ */

// Bell + drop-down panel fed by the activity feed (20s poll). Estimate
// status changes AND payout approvals — declines included, because silence
// on a lost estimate is the adoption killer. Opening marks the feed read;
// events that arrive while the dashboard is mounted fire a sonner toast.

// "Marcus Williams" → "Marcus W." — the consumer gave their name to the shop,
// not to every screen in the building.
function leadShortName(full: string | null | undefined): string | null {
  const trimmed = full?.trim()
  if (!trimmed) return null
  const parts = trimmed.split(/\s+/)
  const first = parts[0]
  const last = parts.length > 1 ? parts[parts.length - 1] : null
  return last ? `${first} ${last[0].toUpperCase()}.` : first
}

function activityLine(e: ActivityEvent): string {
  if (e.kind === "payout") {
    return `Your $${(e.payoutAmount ?? 0).toLocaleString("en-US")} payout for ${e.payoutPeriodLabel ?? "last period"} was approved`
  }
  if (e.kind === "new_lead") {
    const name = leadShortName(e.customerName)
    return `New consumer lead${name ? ` — ${name}` : ""}${e.vehicleLabel ? ` · ${e.vehicleLabel}` : ""}`
  }
  const veh =
    e.vehicleLabel ??
    (e.estimateId != null ? `Estimate #${e.estimateId}` : "A customer")
  if (e.kind === "customer_reply") return `${veh} — customer replied`
  if (e.toStatus === "Declined") return `${veh} — customer declined`
  return `${veh} moved to ${e.toStatus ?? "a new status"}`
}

function timeAgoLabel(iso: string, now: Date): string {
  const diffMs = now.getTime() - new Date(iso).getTime()
  const h = Math.floor(diffMs / (60 * 60 * 1000))
  const d = Math.floor(diffMs / (24 * 60 * 60 * 1000))
  if (h < 1) return "Just now"
  if (h < 24) return `${h}h ago`
  return d === 1 ? "Yesterday" : `${d}d ago`
}

export function NotificationsBell({ teamId }: { teamId: number | null }) {
  const { data } = useActivity(teamId)
  const markRead = useMarkActivityRead(teamId)
  const [open, setOpen] = useState(false)
  // Read marker frozen at open time so rows keep their unread tint while
  // the panel is up (markRead advances the server marker immediately).
  const [openMarkerMs, setOpenMarkerMs] = useState(0)

  const events = data?.events ?? []
  const lastReadMs = data?.lastReadAt ? new Date(data.lastReadAt).getTime() : 0
  const unread = events.filter(
    (e) => new Date(e.occurredAt).getTime() > lastReadMs,
  ).length

  // Toast events that arrive after mount — the poll delivers body-shop
  // status changes while the advisor is standing on the dashboard.
  const newestSeenRef = useRef<{ id: number; at: number } | null>(null)
  useEffect(() => {
    const newest = events[0]
    if (!newest) return
    const at = new Date(newest.occurredAt).getTime()
    const prev = newestSeenRef.current
    if (prev && newest.id !== prev.id && at > prev.at) {
      toast(activityLine(newest))
    }
    newestSeenRef.current = { id: newest.id, at }
  }, [events])

  const toggle = () => {
    setOpen((v) => {
      const next = !v
      if (next) {
        setOpenMarkerMs(lastReadMs)
        if (unread > 0) markRead.mutate()
      }
      return next
    })
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        className="relative size-9 grid place-items-center rounded-xl border border-zinc-200/70 bg-white shadow-sm text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 transition-all duration-150"
      >
        <Bell01 className="size-4" aria-hidden />
        {unread > 0 && !open && (
          <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-emerald-600 text-white text-[9.5px] font-bold grid place-items-center tabular-nums">
            {unread}
          </span>
        )}
      </button>
      {/* Phone-first: activity is a bottom sheet, not a desktop dropdown —
          full-width, grouped by day, room for the quotes and deep links. */}
      <Sheet isOpen={open} onClose={() => setOpen(false)} title="Activity">
        {events.length === 0 ? (
          <div className="py-10 text-center">
            <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
              <Bell01 className="size-5 text-zinc-400" aria-hidden />
            </div>
            <p className="text-[13.5px] font-semibold text-zinc-900">All caught up</p>
            <p className="text-[12.5px] text-zinc-500 mt-1">
              Replies, status moves, and payouts land here.
            </p>
          </div>
        ) : (
          <div className="space-y-4 pb-2">
            {[
              {
                label: "Today",
                rows: events.filter((e) => isSameDay(e.occurredAt)),
              },
              {
                label: "Earlier",
                rows: events.filter((e) => !isSameDay(e.occurredAt)),
              },
            ]
              .filter((g) => g.rows.length > 0)
              .map((g) => (
                <div key={g.label}>
                  <p className="px-1 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                    {g.label}
                  </p>
                  <div className="space-y-0.5">
                    {g.rows.map((e) => (
                      <NotificationRow
                        key={e.id}
                        event={e}
                        unread={new Date(e.occurredAt).getTime() > openMarkerMs}
                        onNavigate={() => setOpen(false)}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </Sheet>
    </div>
  )
}

function isSameDay(iso: string): boolean {
  const d = new Date(iso)
  const n = new Date()
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  )
}

function NotificationRow({
  event,
  unread,
  onNavigate,
}: {
  event: ActivityEvent
  unread: boolean
  onNavigate: () => void
}) {
  const now = new Date()
  const isPayout = event.kind === "payout"
  const isReply = event.kind === "customer_reply"
  const isNewLead = event.kind === "new_lead"
  const isDeclined = event.toStatus === "Declined"
  const isCompleted = event.toStatus === "Completed"
  const Icon = isPayout
    ? Coins03
    : isReply
      ? MessageChatCircle
      : isNewLead
        ? UserPlus01
        : isCompleted
          ? CheckCircle
          : isDeclined
            ? X
            : ArrowRight
  const tile = isPayout
    ? "from-amber-400 to-amber-600 shadow-amber-500/40"
    : isReply
      ? "from-teal-400 to-teal-600 shadow-teal-500/40"
      : isNewLead
        ? "from-emerald-400 to-emerald-600 shadow-emerald-500/40"
        : isCompleted
          ? "from-emerald-400 to-emerald-600 shadow-emerald-500/40"
          : isDeclined
            ? "from-zinc-300 to-zinc-500 shadow-zinc-400/40"
            : "from-sky-400 to-sky-600 shadow-sky-500/40"
  // A reply deep-links to the customer's thread (where you reply); everything
  // else opens the estimate. Push, don't make them look — the reply comes here.
  const to = isPayout
    ? "/wallet"
    : isReply
      ? event.customerId != null
        ? `/customers/${event.customerId}`
        : event.estimateId != null
          ? `/estimates/${event.estimateId}`
          : null
      : event.estimateId != null
        ? `/estimates/${event.estimateId}`
        : null
  const cls = cx(
    "flex items-start gap-2.5 px-2.5 py-2.5 rounded-xl transition-colors duration-100",
    unread && "bg-emerald-50/40",
    to && "hover:bg-zinc-50",
  )
  const body = (
    <>
      <div
        className={cx(
          "size-8 rounded-lg grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
          tile,
        )}
      >
        <Icon className="size-3.5 text-white" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-zinc-900 leading-snug">
          {activityLine(event)}
        </p>
        {isDeclined && (
          <p className="text-[11px] text-zinc-500 leading-snug mt-0.5">
            It happens — the scan still counted.
          </p>
        )}
        {isReply && event.body && (
          <p className="text-[11px] text-zinc-500 leading-snug mt-0.5 line-clamp-1 italic">
            “{event.body}”
          </p>
        )}
        <p className="text-[10.5px] text-zinc-400 mt-0.5">
          {timeAgoLabel(event.occurredAt, now)}
        </p>
      </div>
      {unread && (
        <span className="size-1.5 rounded-full bg-emerald-500 shrink-0 mt-1.5" aria-hidden />
      )}
    </>
  )
  return to ? (
    <Link to={to} onClick={onNavigate} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

